import { yupResolver } from '@hookform/resolvers';
import { Box, Button, Dialog, DialogContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useBenchmarkState } from '../../pages/Benchmark/Context/BenchmarkContext';
import { useErrorsDispatch } from '../../pages/FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../pages/FinancialData/shared/ErrorContext/ErrorContext.types';
import InputField from '../Inputs/TextField';
import DialogTitleBlock from './Dialog/DialogTitleBlock';

interface SubmitActionRequestBody {
  name: string;
}

interface OwnProps {
  show: boolean;
  title: string;
  defaultValue?: string;
  handleSave: () => void;
  handleClose: () => void;
  submitAction: (benchmarkId: string, req: SubmitActionRequestBody) => any;
}

const BenchmarkModal: React.FC<OwnProps> = ({
  show,
  title,
  defaultValue,
  handleClose,
  handleSave,
  submitAction,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const { activeBenchmark } = useBenchmarkState();
  const dispatchErrors = useErrorsDispatch();
  const { register, handleSubmit, errors, setError } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        benchmarkTitle: yup.string().required('companies:requiredField'),
      }),
    ),
  });

  const onSubmit = async (formData: any) => {
    setDisabled(true);
    try {
      if (await submitAction(activeBenchmark?.id!, { name: formData.benchmarkTitle })) {
        handleSave();
      }
    } catch (error) {
      if (error?.data?.error === 'BENCHMARK_DUPLICATE_NAME') {
        setError('benchmarkTitle', {
          message: `'${formData.benchmarkTitle}' is already used`,
          type: 'required',
        });
      } else if (error?.data?.error === 'BENCHMARK_COMPANY_LIMIT_EXCEEDED') {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error, count: error?.data?.detail },
        });
      } else {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error },
        });
      }
    } finally {
      setDisabled(false);
    }
  };

  return (
    <Box>
      <Dialog
        disableRestoreFocus={true}
        onClose={() => {
          handleClose();
        }}
        open={show}
        className={classes.dialogBox}
      >
        <DialogTitleBlock
          icon={<TuneIcon />}
          title={title}
          handleClose={handleClose}
          disabled={disabled}
        />
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputField
              label={t('benchmark:sendToBenchmark.nameField')}
              name="benchmarkTitle"
              mandatory={true}
              refReg={register}
              defaultValue={defaultValue}
              errors={errors}
              focus
            />
            <Button type="submit" color="primary" variant="contained" disabled={disabled}>
              {t('app:save')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  input: {
    padding: theme.spacing(0),
  },
  dialogBox: {
    '& .MuiDialogContent-root .MuiPaper-root': {
      borderRadius: 0,
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    color: theme.palette.grey[500],
  },
}));

export default BenchmarkModal;
